/**
 * API Error Handlers (ESM)
 *
 * Utility functions for API error handling including status code mapping,
 * error creation, processing, formatting, and retry logic.
 *
 * @module @puffin-core/error-handling/handlers/apiErrorHandlers
 */

import { API_ERROR_CODES } from '../constants/errorConstants.js';
import { eachErrorInChain, readErrorCode } from '../errorCode.js';

// The ONE reader lives in the zero-import `../errorCode.js` (served at `./error-code`); this module re-exports it so
// its own surface and the barrel's are unchanged.
export { readErrorCode };

/**
 * The server's machine code for an expired token, as emitted by
 * `@puffin-core/response-helpers`' `classifyError` and `sendTokenExpired`. Distinct from `API_ERROR_CODES.TOKEN_EXPIRED`, which is
 * this package's CLIENT-side classification.
 */
const SERVER_TOKEN_EXPIRED_CODE = 'TOKEN_EXPIRED';

/**
 * Each failure class's display slot and level: the ONE place a class picks its words, which live in namespace #57
 * `errorHandling` as `display.<slot>.title` / `display.<slot>.message` (the ESM-only `./strings` leaf). This package
 * composes no display copy (#57, THINKPAD's option (D) and Q-57-1 (b)). A class not listed reads `unknown`.
 */
const DISPLAY_OF_CLASS = Object.freeze({
  [API_ERROR_CODES.NETWORK_ERROR]: Object.freeze({ slot: 'network', level: 'warning' }),
  [API_ERROR_CODES.TIMEOUT]: Object.freeze({ slot: 'timeout', level: 'warning' }),
  [API_ERROR_CODES.UNAUTHORIZED]: Object.freeze({ slot: 'authentication', level: 'warning' }),
  [API_ERROR_CODES.TOKEN_EXPIRED]: Object.freeze({ slot: 'authentication', level: 'warning' }),
  [API_ERROR_CODES.FORBIDDEN]: Object.freeze({ slot: 'forbidden', level: 'warning' }),
  [API_ERROR_CODES.NOT_FOUND]: Object.freeze({ slot: 'notFound', level: 'warning' }),
  [API_ERROR_CODES.VALIDATION_ERROR]: Object.freeze({ slot: 'validation', level: 'warning' }),
  [API_ERROR_CODES.SERVER_ERROR]: Object.freeze({ slot: 'server', level: 'error' }),
});

/** The display of a class no entry names, of a non-API error, and of no error at all. */
const UNKNOWN_DISPLAY = Object.freeze({ slot: 'unknown', level: 'error' });

/**
 * @param {string} failureClass - an `API_ERROR_CODES` class (`failureClassOf`)
 * @returns {{slot: string, level: string}}
 */
function displayOfClass(failureClass) {
  return Object.prototype.hasOwnProperty.call(DISPLAY_OF_CLASS, failureClass)
    ? DISPLAY_OF_CLASS[failureClass]
    : UNKNOWN_DISPLAY;
}

/**
 * @param {string} slot
 * @returns {{titleKey: string, messageKey: string}} the slot's two #57 keys
 */
function displayKeysOf(slot) {
  return { titleKey: `display.${slot}.title`, messageKey: `display.${slot}.message` };
}

/**
 * Is `body` the canonical RES-ENV-001 error envelope?
 *
 *   { success: false, error: { code, message, details? } }
 *
 * Byte-for-byte mirror of the CJS half. Kept in step by
 * `tests/esm/native-import.test.mjs` (export surface) and by this package's
 * unit suite (behaviour).
 *
 * @param {*} body
 * @returns {boolean}
 */
function isCanonicalErrorBody(body) {
  return (
    typeof body === 'object' &&
    body !== null &&
    body.success === false &&
    typeof body.error === 'object' &&
    body.error !== null &&
    !Array.isArray(body.error)
  );
}

/**
 * Map HTTP status codes to error types
 *
 * @param {number} statusCode - HTTP status code
 * @returns {string} Error code
 */
export function mapStatusCodeToErrorCode(statusCode) {
  switch (statusCode) {
    case 400:
      return API_ERROR_CODES.BAD_REQUEST;
    case 401:
      return API_ERROR_CODES.UNAUTHORIZED;
    case 403:
      return API_ERROR_CODES.FORBIDDEN;
    case 404:
      return API_ERROR_CODES.NOT_FOUND;
    case 408:
      return API_ERROR_CODES.TIMEOUT;
    case 409:
      return API_ERROR_CODES.CONFLICT;
    case 422:
      return API_ERROR_CODES.VALIDATION_ERROR;
    case 500:
      return API_ERROR_CODES.SERVER_ERROR;
    case 503:
      return API_ERROR_CODES.SERVICE_UNAVAILABLE;
    default:
      return statusCode >= 500
        ? API_ERROR_CODES.SERVER_ERROR
        : statusCode >= 400
          ? API_ERROR_CODES.BAD_REQUEST
          : API_ERROR_CODES.UNKNOWN_ERROR;
  }
}

/**
 * Create a standardized API error
 *
 * @param {Object} options - Error options
 * @param {?string} [options.code] - The transport's own code (axios's `ERR_NETWORK`, Node's
 *   `ECONNABORTED`), or none. Never a class: the class is `failureClassOf(error)`, judged from the facts.
 * @param {string} options.message - The error's LOG line: never display copy (#57, Q-57-1 (b)). A reader shows
 *   `serverMessage ?? t(messageKey)` (`formatApiErrorForDisplay`).
 * @param {number} [options.status] - HTTP status code
 * @param {Object} [options.data] - Additional error data
 * @param {Error} [options.originalError] - Original error
 * @param {string} [options.name] - Override `error.name` (e.g. 'AbortError' so
 *   canonical `err.name === 'AbortError'` cancellation guards keep working
 *   after the raw axios/fetch error is normalized).
 * @param {string|null} [options.errorCode=null] - The SERVER's RES-ENV-001 business code
 *   (`PUNCH_RUN_LOCKED`), distinct from `code`, the transport's.
 * @param {*|null} [options.errorDetails=null] - The server's `error.details`, verbatim.
 * @param {string|null} [options.serverMessage=null] - The server's OWN sentence, from the canonical envelope's
 *   `error.message`, or null when no server spoke one. The only sentence a reader may show as it is.
 * @returns {Error} Standardized API error. It also carries `messageKey`, the #57 key of its class's display copy.
 */
export function createApiError({
  code,
  message,
  status,
  data,
  originalError,
  name,
  errorCode = null,
  errorDetails = null,
  serverMessage = null,
}) {
  const apiError = new Error(message);

  // Preserve the canonical error name when supplied. `new Error()` defaults
  // `name` to 'Error', which silently breaks the ecosystem-wide
  // `err?.name === 'AbortError'` cancellation guard for normalized aborts.
  if (name) {
    apiError.name = name;
  }

  // Add API error properties
  apiError.code = code ?? null;
  apiError.status = status;
  apiError.data = data;
  apiError.isApiError = true;
  // Q-RC-RESPONSE-ARM (2026-08-28): the canonical TOP-LEVEL vocabulary, shared verbatim with
  // `@puffin-core/api-client`'s `NormalizedError` and the RES-CLIENT-001 Phase-2 `ApiError`. Every
  // web app reads the server's code and detail as `errorCode` / `errorDetails`; a consumer no
  // longer needs to know WHICH client fed its catch. `data` (the flattened error) stays for the
  // `isApiError` readers Phase 2 retires. `null`, never `undefined`: "no code" and "a field this
  // producer forgot" must stay distinguishable.
  apiError.errorCode = errorCode ?? null;
  apiError.errorDetails = errorDetails ?? null;
  apiError.serverMessage = typeof serverMessage === 'string' && serverMessage !== '' ? serverMessage : null;

  // Preserve original error information
  if (originalError) {
    apiError.originalError = originalError;
    apiError.originalStack = originalError.stack;
  }

  // The class's display copy, as a KEY into namespace #57: what a site shows when no server spoke (#57). Judged LAST,
  // from every fact the error now carries (`failureClassOf` reads the original error's transport code too).
  apiError.messageKey = displayKeysOf(displayOfClass(failureClassOf(apiError)).slot).messageKey;

  return apiError;
}

/**
 * The HTTP failure an error carries — its status and response body — from EITHER shape that reaches
 * `processApiError`, or `null` when the request never got an answer.
 *
 *  - an axios rejection: `error.response`;
 *  - the api FACADE's `ApiError` (`@puffin-core/api-client`, RES-CLIENT-001), recognised by its own
 *    fields (`name: 'ApiError'`, a numeric `status`) because `api-client` is ESM-only and cannot be
 *    imported by this module's CommonJS half. It carries the status and the full body (`.body`) and
 *    deliberately NO `.response`, so until this reader every served facade refusal fell into the "no
 *    response" arm and came back labelled NETWORK_ERROR (L16-PASSWORD-FOLLOWONS-001 §1.2: the signup
 *    wizard toasted "A network error occurred" for a password the server had judged). Status 0 means
 *    the request never got an answer; that stays a network / abort / timeout failure.
 *
 * @param {Error|Object} error
 * @returns {{status: number, data: *}|null}
 */
function readHttpFailure(error) {
  if (error.response) {
    return { status: error.response.status, data: error.response.data };
  }
  if (error.name === 'ApiError' && typeof error.status === 'number' && error.status > 0) {
    return { status: error.status, data: error.body };
  }
  return null;
}

/** Transport codes for a request that ran out of time before any answer (axios, Node's socket). */
const TIMEOUT_TRANSPORT_CODES = Object.freeze(['ECONNABORTED', 'ETIMEDOUT']);

/** Transport codes for a request the client cancelled (axios v1 `ERR_CANCELED`). */
const CANCELLED_TRANSPORT_CODES = Object.freeze(['ERR_CANCELED']);

/**
 * The codes a TRANSPORT writes on a request that failed — axios's, Node's socket errno codes, and
 * `@puffin-core/auth-client`'s own network sentinel. A closed list: a class string a producer stored on
 * `.code` (`'network_error'`) or a server code (`'VALIDATION_ERROR'`) is evidence of nothing.
 */
const TRANSPORT_FAILURE_CODES = Object.freeze([
  ...TIMEOUT_TRANSPORT_CODES,
  ...CANCELLED_TRANSPORT_CODES,
  'ERR_NETWORK',
  'ERR_BAD_REQUEST',
  'ERR_BAD_RESPONSE',
  'ERR_FR_TOO_MANY_REDIRECTS',
  'ECONNREFUSED',
  'ECONNRESET',
  'ENOTFOUND',
  'EHOSTUNREACH',
  'ENETUNREACH',
  'EAI_AGAIN',
  'EPIPE',
  'NETWORK_ERROR',
]);

/**
 * The status of the server's answer, in whichever shape the error carries it: an axios rejection's
 * `response.status`, or a numeric `status` (the api facade's `ApiError`, `@puffin-core/auth-client`'s
 * `authError`, a processed error). `null` when no server answered — status 0 is the facade's
 * "never answered" sentinel.
 *
 * @param {Object} error
 * @returns {?number}
 */
function answeredStatusOf(error) {
  const status = error.response ? error.response.status : error.status;
  return typeof status === 'number' && status > 0 ? status : null;
}

/**
 * The server's business code (RES-ENV-001 `error.code`): the hoisted `errorCode` every client error
 * carries, or, on a raw axios rejection, the canonical envelope's own code.
 *
 * @param {Object} error
 * @returns {?string}
 */
function serverCodeOf(error) {
  if (typeof error.errorCode === 'string') return error.errorCode;
  const body = error.response ? error.response.data : null;
  return isCanonicalErrorBody(body) && typeof body.error.code === 'string' ? body.error.code : null;
}

/**
 * The transport's own code on a failure (axios's `ERR_NETWORK`, Node's `ECONNABORTED`): the error's
 * `code` (a processed error keeps the transport's code there), else the code of the error a processed
 * error was built from.
 *
 * @param {Object} error
 * @returns {?string}
 */
function transportCodeOf(error) {
  if (typeof error.code === 'string') return error.code;
  const source = error.originalError;
  return source && typeof source.code === 'string' ? source.code : null;
}

/**
 * Whether an error came from a REQUEST, so its missing answer is a network failure, not a bug: a
 * processed error, an axios rejection, the api facade's `ApiError` (it always carries a numeric
 * status), a `NetworkError`, or an error carrying a {@link TRANSPORT_FAILURE_CODES} code itself or on
 * its `cause` (undici's `fetch failed` puts the errno there).
 * A bare `TypeError` thrown by code is none of these.
 *
 * @param {Object} error
 * @returns {boolean}
 */
function isRequestFailure(error) {
  return (
    error.isApiError === true ||
    error.isAxiosError === true ||
    error.name === 'AxiosError' ||
    (error.name === 'ApiError' && typeof error.status === 'number') ||
    error.name === 'NetworkError' ||
    TRANSPORT_FAILURE_CODES.includes(transportCodeOf(error)) ||
    TRANSPORT_FAILURE_CODES.includes(error.cause?.code)
  );
}

/**
 * What kind of failure an error is: one of `API_ERROR_CODES`' classes, judged from its FACTS — its
 * name, whether a server answered, the status, the server's code — never from a class stored on it and
 * never from its message (D1 errorCode unit, the converged change). It judges every shape that reaches
 * a client: a raw axios rejection, the api facade's `ApiError`, an auth-client error, a processed error.
 *
 *  1. an abort (`AbortError`, axios's `ERR_CANCELED`) → `request_aborted`;
 *  2. no answer: a timeout (`TimeoutError`, `ECONNABORTED`, `ETIMEDOUT`) → `request_timeout`; a
 *     request that failed ({@link isRequestFailure}) → `network_error`; anything else is not a request
 *     failure at all → `unknown_error`;
 *  3. a 401: the server's `TOKEN_EXPIRED` → `token_expired`, else `unauthorized` — no message
 *     fallback, whose only live effect was a misread (a SHIFT_TOKEN_INVALID "invalid or expired"
 *     shown as an expired session);
 *  4. any other status → {@link mapStatusCodeToErrorCode}.
 *
 * @param {*} error
 * @returns {string} an `API_ERROR_CODES` class
 */
export function failureClassOf(error) {
  if (!error || typeof error !== 'object') return API_ERROR_CODES.UNKNOWN_ERROR;

  const transportCode = transportCodeOf(error);
  if (error.name === 'AbortError' || CANCELLED_TRANSPORT_CODES.includes(transportCode)) {
    return API_ERROR_CODES.ABORTED;
  }

  const status = answeredStatusOf(error);
  if (status === null) {
    if (error.name === 'TimeoutError' || TIMEOUT_TRANSPORT_CODES.includes(transportCode)) {
      return API_ERROR_CODES.TIMEOUT;
    }
    return isRequestFailure(error) ? API_ERROR_CODES.NETWORK_ERROR : API_ERROR_CODES.UNKNOWN_ERROR;
  }

  if (status === 401) {
    return serverCodeOf(error) === SERVER_TOKEN_EXPIRED_CODE
      ? API_ERROR_CODES.TOKEN_EXPIRED
      : API_ERROR_CODES.UNAUTHORIZED;
  }

  return mapStatusCodeToErrorCode(status);
}

/**
 * Process a raw error response into a standardized API error
 *
 * @param {Error|Object} error - Error response
 * @returns {Error} Standardized API error
 */
export function processApiError(error) {
  // Already processed
  if (error.isApiError) {
    return error;
  }

  const httpFailure = readHttpFailure(error);

  // The class is judged ONCE, from the error's facts (`failureClassOf`); `.code` keeps only the
  // transport's own code (D1 errorCode unit, the converged change).
  const failureClass = failureClassOf(error);
  const code = transportCodeOf(error);

  // No answer: a timeout, an abort, or a network failure.
  if (!httpFailure) {
    if (failureClass === API_ERROR_CODES.TIMEOUT) {
      return createApiError({
        code,
        message: 'Request timed out. Please try again.',
        originalError: error,
      });
    }

    if (failureClass === API_ERROR_CODES.ABORTED) {
      return createApiError({
        code,
        // Canonical DOM abort name so every `err?.name === 'AbortError'`
        // cancellation guard across the ecosystem (e.g. pos-web useOrgChart and
        // ~50 sibling data hooks) correctly ignores StrictMode/unmount aborts
        // instead of logging a spurious error + setting an error state.
        name: 'AbortError',
        message: 'Request was aborted.',
        originalError: error,
      });
    }

    return createApiError({
      code,
      message: 'A network error occurred. Please check your connection and try again.',
      originalError: error,
    });
  }

  // Handle an error that got an answer (an axios response, or a served facade `ApiError`).
  // Reaching this point guarantees `httpFailure` is present — the branch above returned for
  // every answerless error — so no separate catch-all fallback is needed after this block.
  {
    const { status, data } = httpFailure;

    // RES-ENV-001: the server speaks exactly ONE error shape,
    // `{ success:false, error:{ code, message, details? } }`. This used to be a
    // dual-shape reader (`typeof data.error === 'string' ? … : …`) precisely
    // because three incompatible envelopes were in flight. They are gone; so is
    // the branch. A non-JSON body (a proxy's HTML 502) still degrades cleanly.
    let errorMessage = 'An error occurred.';
    let errorData = {};

    if (typeof data === 'string') {
      errorMessage = data;
    } else if (isCanonicalErrorBody(data)) {
      errorMessage = data.error.message || 'An error occurred.';
      errorData = { ...data.error };
    } else if (data) {
      errorData = data;
    }

    // The hoisted top-level vocabulary (see `createApiError`). Read from the CANONICAL body only,
    // exactly as `@puffin-core/api-client`'s `unwrapEnvelope` does: an opaque gateway JSON that
    // happens to carry a `code` member is not the server's business code.
    const envelope = isCanonicalErrorBody(data) ? data.error : null;
    const serverCode = envelope?.code ?? null;
    const serverDetails = envelope?.details ?? null;
    // The server's OWN sentence, from the canonical envelope only: a string body (a proxy's HTML page) and this
    // function's `'An error occurred.'` default are never a sentence a reader shows (#57).
    const serverMessage = envelope && typeof envelope.message === 'string' ? envelope.message : null;

    // Validation details ride in `error.details` on the canonical envelope.
    // They previously sat at a top-level `data.errors` that no producer wrote.
    if (failureClass === API_ERROR_CODES.VALIDATION_ERROR && errorData.details) {
      return createApiError({
        code,
        message: errorMessage,
        status,
        data: {
          ...errorData,
          validationErrors: errorData.details,
        },
        originalError: error,
        errorCode: serverCode,
        errorDetails: serverDetails,
        serverMessage,
      });
    }

    // An expired session (the server's TOKEN_EXPIRED on a 401, `failureClassOf`) and any other 401.
    if (failureClass === API_ERROR_CODES.TOKEN_EXPIRED) {
      return createApiError({
        code,
        message: 'Your session has expired. Please log in again.',
        status,
        data: errorData,
        originalError: error,
        errorCode: serverCode,
        errorDetails: serverDetails,
      });
    }

    if (failureClass === API_ERROR_CODES.UNAUTHORIZED) {
      return createApiError({
        code,
        message: 'Authentication required. Please log in to continue.',
        status,
        data: errorData,
        originalError: error,
        errorCode: serverCode,
        errorDetails: serverDetails,
      });
    }

    // General error with response
    return createApiError({
      code,
      message: errorMessage,
      status,
      data: errorData,
      originalError: error,
      errorCode: serverCode,
      errorDetails: serverDetails,
      serverMessage,
    });
  }
}

/**
 * The sentence a reader may show as it is: the server's own (`serverMessage`), and only when a server answered.
 *
 * @param {Object} apiError - a processed error
 * @returns {?string}
 */
function shownServerSentenceOf(apiError) {
  if (answeredStatusOf(apiError) === null) return null;
  return typeof apiError.serverMessage === 'string' && apiError.serverMessage !== ''
    ? apiError.serverMessage
    : null;
}

/**
 * What a reader shows for an error, as KEYS: this package composes no display words (#57, THINKPAD's option (D) and
 * Q-57-1 (b)). A host renders `message ?? t(messageKey)` under `t(titleKey)`, translating through namespace #57
 * `errorHandling` (`display.<slot>.title` / `.message`).
 *
 * - `message` is a sentence ANOTHER party composed: the server's own, carried by `processApiError` as
 *   `serverMessage`, and only when a server answered. Never the error's `.message`, which is a log line (and, on a
 *   non-API error, a bug's text).
 * - The slot and level come from the error's class, judged from its facts (`failureClassOf`). A non-API error and
 *   no error at all read the `unknown` slot.
 *
 * @param {?Error} apiError - an error, processed or not, or nothing
 * @returns {{level: string, titleKey: string, messageKey: string, message: ?string, validationErrors?: *}}
 */
export function formatApiErrorForDisplay(apiError) {
  const isApiError = Boolean(apiError && apiError.isApiError);
  const { slot, level } = isApiError ? displayOfClass(failureClassOf(apiError)) : UNKNOWN_DISPLAY;
  const display = {
    level,
    ...displayKeysOf(slot),
    message: isApiError ? shownServerSentenceOf(apiError) : null,
  };
  if (slot === 'validation') {
    display.validationErrors = apiError.data?.validationErrors;
  }
  return display;
}

/**
 * Determine if an error should trigger a retry
 *
 * @param {Error} apiError - API error
 * @returns {boolean} Whether the error is retryable
 */
export function isRetryableError(apiError) {
  if (!apiError) {
    return false;
  }

  // Don't retry these errors
  const nonRetryableCodes = [
    API_ERROR_CODES.UNAUTHORIZED,
    API_ERROR_CODES.FORBIDDEN,
    API_ERROR_CODES.NOT_FOUND,
    API_ERROR_CODES.VALIDATION_ERROR,
    API_ERROR_CODES.CONFLICT,
  ];

  // The class is judged from the facts, so a raw or facade error is classed too, not only a processed one.
  const failureClass = failureClassOf(apiError);
  if (nonRetryableCodes.includes(failureClass)) {
    return false;
  }

  // Retry network errors, timeouts, and server errors
  const retryableCodes = [
    API_ERROR_CODES.NETWORK_ERROR,
    API_ERROR_CODES.TIMEOUT,
    API_ERROR_CODES.SERVER_ERROR,
    API_ERROR_CODES.SERVICE_UNAVAILABLE,
  ];

  return retryableCodes.includes(failureClass);
}

/**
 * Get appropriate retry delay based on error and attempt count
 *
 * @param {Error} apiError - API error
 * @param {number} attempt - Current retry attempt (0-based)
 * @param {Object} [options={}] - Retry options
 * @param {number} [options.baseDelay=1000] - Base delay in milliseconds
 * @param {number} [options.maxDelay=30000] - Maximum delay in milliseconds
 * @returns {number} Retry delay in milliseconds
 */
export function getRetryDelay(apiError, attempt, options = {}) {
  const { baseDelay = 1000, maxDelay = 30000 } = options;

  // Exponential backoff with jitter
  let delay = baseDelay * Math.pow(2, attempt);

  // Add jitter (±20%)
  const jitter = delay * 0.2 * (Math.random() * 2 - 1);
  delay += jitter;

  // Special case for service unavailable - use Retry-After header if present
  if (failureClassOf(apiError) === API_ERROR_CODES.SERVICE_UNAVAILABLE) {
    const retryAfter = (apiError.originalError ?? apiError)?.response?.headers?.['retry-after'];
    if (retryAfter) {
      // Retry-After can be a timestamp or seconds
      if (isNaN(retryAfter)) {
        // Timestamp format
        const retryDate = new Date(retryAfter);
        if (!isNaN(retryDate.getTime())) {
          return Math.max(0, retryDate.getTime() - Date.now());
        }
      } else {
        // Seconds format
        return parseInt(retryAfter, 10) * 1000;
      }
    }
  }

  // Cap at max delay
  return Math.min(delay, maxDelay);
}

/**
 * Read one bounded machine token out of an api error's `details`, in either error shape.
 *
 * The generalisation of {@link readRateLimiterToken}: `details` is where a variant travels when the
 * CODE must stay singular. Rate limiting is one condition with one code and 27 user-reachable
 * surfaces; the same shape serves any error whose code is deliberately coarse.
 *
 * @param {*} error - a processed api error, or a raw axios error
 * @param {string} key - the `details` property holding the token (e.g. `'limiter'`, `'reason'`)
 * @returns {?string} the token, or `null` when absent/not a string
 */
export function readErrorDetail(error, key) {
  for (const e of eachErrorInChain(error)) {
    // Q-RC-RESPONSE-ARM (2026-08-28): `errorDetails` is the hoisted, canonical carrier.
    const fromProcessed = e?.errorDetails?.[key] ?? e?.data?.details?.[key];
    const fromRaw = e?.response?.data?.error?.details?.[key];
    const token = typeof fromProcessed === 'string' ? fromProcessed : fromRaw;
    if (typeof token === 'string' && token.length > 0) return token;
  }
  return null;
}

/**
 * Read an api error's whole `details` object, in either error shape.
 *
 * {@link readErrorDetail} reads one bounded machine TOKEN; this reads a refusal's STRUCTURE — the
 * details a client renders rather than branches on (a compliance checker's result travels with a
 * `MARKETPLACE_CLAIM_COMPLIANCE_BLOCKED`). Reading `err.errorDetails` directly would know only the
 * processed shape and stop at the first wrapper; this walks the `cause` chain like every reader here.
 *
 * @param {*} error - a processed api error, or a raw axios error
 * @returns {?Object} the nearest `details` that is a plain object, or `null`
 */
export function readErrorDetails(error) {
  for (const e of eachErrorInChain(error)) {
    // Q-RC-RESPONSE-ARM (2026-08-28): `errorDetails` is the hoisted, canonical carrier.
    const carriers = [e?.errorDetails, e?.data?.details, e?.response?.data?.error?.details];
    const details = carriers.find(
      (candidate) =>
        candidate !== null && typeof candidate === 'object' && !Array.isArray(candidate),
    );
    if (details) return details;
  }
  return null;
}

/**
 * Resolve any coded api error to the caller's OWN catalog leaf, with a mandatory generic fallback.
 *
 * The canonical shape for code → leaf resolution across the ecosystem. Catalog-agnostic on purpose:
 * a shared package must not own app catalog paths, and a shared REGISTRY of known codes would be a
 * second copy of the server's vocabulary — the ENUM-DRIFT-001 shape where two lists that must agree
 * have nothing comparing them. The caller's own map IS the bound, so an unmapped code degrades to
 * the generic leaf and never breaks.
 *
 * `byDetail` is consulted FIRST when supplied, because a `details` token is strictly more specific
 * than the code that carries it. Both are optional; supplying neither yields `genericLeaf`.
 *
 * @param {*} error - a processed api error, or a raw axios error
 * @param {Object} spec
 * @param {Object<string, string>} [spec.byCode] - code → catalog leaf
 * @param {Object<string, string>} [spec.byDetail] - `details[detailKey]` token → catalog leaf
 * @param {string} [spec.detailKey] - which `details` property `byDetail` is keyed on
 * @param {string} spec.genericLeaf - leaf used when nothing matches. NOT optional, by design.
 * @returns {string} a catalog leaf path
 */
export function resolveErrorLeaf(error, { byCode, byDetail, detailKey, genericLeaf } = {}) {
  const usable = (leaf) => (typeof leaf === 'string' && leaf.length > 0 ? leaf : null);
  if (byDetail && detailKey) {
    const token = readErrorDetail(error, detailKey);
    const hit = token === null ? null : usable(byDetail[token]);
    if (hit) return hit;
  }
  if (byCode) {
    const code = readErrorCode(error);
    const hit = code === null ? null : usable(byCode[code]);
    if (hit) return hit;
  }
  return genericLeaf;
}

/**
 * Read the `details.limiter` machine token off a 429, in either error shape.
 *
 * ## Why a token and not a code (Q-R39-1, user ruling 7, 2026-08-10)
 *
 * Rate limiting is ONE error condition and keeps ONE code, `RATE_LIMIT_EXCEEDED` — the
 * RES-ENV-001 vocabulary does not grow a `…_MFA` / `…_EXPORT` family. But the server's
 * limiters carry per-endpoint information the shared code does not: measured across the
 * repo, **111 `createRateLimiter` sites, 61 with a bespoke English `message`, 57 of those on
 * user-reachable routes, collapsing to 27 distinct surfaces**. A client that maps only the
 * code renders correct Spanish and loses every one of those distinctions; a client that
 * renders the server's `message` renders English.
 *
 * So the variant travels as a bounded machine token in `details`, minted from
 * `createRateLimiter`'s existing closed-cardinality `name` (the same label already used as
 * the `rate_limit_exceeded_total` `surface` metric attribute — one label, two consumers, no
 * second vocabulary).
 *
 * ## Two shapes, deliberately
 *
 * `processApiError` flattens the envelope to `err.data = {...data.error}`, so a processed
 * error carries `err.data.details.limiter`. A caller that has NOT processed the error yet
 * still holds the raw axios shape. Reading both is what stops adopters inventing their own
 * accessor — the accessor is where a typo silently becomes "no token", which is
 * indistinguishable from an unnamed limiter.
 *
 * @param {*} error - a processed api error, or a raw axios error
 * @returns {?string} the limiter token, or `null` when absent/not a string
 */
export function readRateLimiterToken(error) {
  return readErrorDetail(error, 'limiter');
}

/**
 * Resolve a 429 to the caller's OWN catalog leaf, with a mandatory generic fallback.
 *
 * Catalog-agnostic on purpose. A shared package must not own app catalog paths, and a shared
 * REGISTRY of known tokens would be a second copy of the server's 27 surfaces — exactly the
 * ENUM-DRIFT-001 shape this initiative keeps finding, where two lists that must agree have
 * nothing comparing them. The caller's own map IS the bound: a token it does not know falls
 * back, so an unmapped or absent token degrades and never breaks.
 *
 * @param {*} error - a processed api error, or a raw axios error
 * @param {Object<string, string>} leafByLimiter - the caller's token → catalog-leaf map
 * @param {string} genericLeaf - leaf used when the token is absent or unmapped
 * @returns {string} a catalog leaf path
 */
export function resolveRateLimitLeaf(error, leafByLimiter, genericLeaf) {
  return resolveErrorLeaf(error, { byDetail: leafByLimiter, detailKey: 'limiter', genericLeaf });
}

export { API_ERROR_CODES };
